import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, LogIn } from 'lucide-react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';

const SlideOutMobileMenu = ({ isOpen, onClose, navLinks }) => {
  const location = useLocation();
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  const user = isAuthenticated ? customer : null;
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef(null);

  // Handle responsive breakpoint - detect phones and tablets
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isPhone = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // Always mobile if it's a phone/tablet, OR if width <= 1024px
      setIsMobile(isPhone || (isTouchDevice && width <= 1024) || width <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', () => {
      // Small delay to let orientation change complete
      setTimeout(checkMobile, 100);
    });
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    console.log('📱 [MENU DEBUG] Menu state changed - isOpen:', isOpen, 'isMobile:', isMobile);
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
      console.log('📱 [MENU DEBUG] Body scroll locked');
    } else {
      document.body.style.overflow = 'unset';
      console.log('📱 [MENU DEBUG] Body scroll unlocked');
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      console.log('📱 [MENU DEBUG] Click outside detected');
      console.log('📱 [MENU DEBUG] Event target:', event.target);
      console.log('📱 [MENU DEBUG] Menu ref current:', menuRef.current);
      console.log('📱 [MENU DEBUG] Is menu toggle?', event.target.closest('[data-mobile-menu-toggle]'));
      
      // Don't close menu if clicking on cart panel or cart buttons
      const isCartClick = event.target.closest('[class*="rainbow-chatbot"]') || 
                          event.target.closest('button[aria-label*="cart" i]') ||
                          event.target.closest('button[aria-label*="Cart" i]') ||
                          event.target.closest('[data-cart-button]');
      
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          !event.target.closest('[data-mobile-menu-toggle]') &&
          !isCartClick) {
        console.log('📱 [MENU DEBUG] Closing menu due to outside click');
        onClose();
      } else {
        console.log('📱 [MENU DEBUG] Click ignored (inside menu, on toggle button, or cart-related)');
      }
    };

    if (isOpen) {
      console.log('📱 [MENU DEBUG] Menu is open, adding click outside listener');
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      console.log('📱 [MENU DEBUG] Removing click outside listener');
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNavLinkClick = () => {
    console.log('📱 [MENU DEBUG] Nav link clicked, closing menu');
    onClose();
  };

  const handleLogout = () => {
    logout();
    handleNavLinkClick();
  };

  // Use React Portal to render directly to document.body
  // This ensures the menu is completely outside the App container and navbar
  // Same approach used for modals and overlays
  // Show menu if it's open AND (mobile OR width is small) - this prevents menu from disappearing on rotation
  const shouldShowMenu = isOpen && (isMobile || window.innerWidth <= 1024);
  
  const menuContent = (
    <AnimatePresence>
      {shouldShowMenu && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              console.log('📱 [MENU DEBUG] Backdrop clicked');
              console.log('📱 [MENU DEBUG] Event target:', e.target);
              e.stopPropagation();
              onClose();
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998
            }}
          />
          
          {/* Menu Panel - Slides in from left */}
          <motion.div
            ref={menuRef}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              width: isMobile ? 'auto' : '400px',
              minWidth: isMobile ? '280px' : '400px',
              maxWidth: isMobile ? '85vw' : '400px',
              height: '100vh',
              position: 'fixed',
              left: 0,
              top: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(31, 41, 55, 0.95))',
              backdropFilter: 'blur(20px)',
              borderRight: '2px solid rgba(6, 182, 212, 0.3)',
              boxShadow: '0 0 50px rgba(0, 0, 0, 0.8), 0 0 100px rgba(6, 182, 212, 0.2)',
              zIndex: 9999,  // Highest z-index to ensure it's above everything
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden',
              overflowY: 'auto',
              boxSizing: 'border-box'
            }}
          >
            {/* Menu Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              borderBottom: '2px solid rgba(6, 182, 212, 0.3)',
              background: 'rgba(0, 0, 0, 0.6)',
              width: '100%',
              boxSizing: 'border-box',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Menu style={{ width: '24px', height: '24px', color: '#ec4899' }} />
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: 0,
                  background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}>
                  Menu
                </h2>
              </div>
              <button
                onClick={onClose}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                }}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
          
            {/* Navigation Links */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              padding: '0px 24px 24px 24px',
              minHeight: 0,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleNavLinkClick}
                  style={{
                    display: 'block',
                    color: location.pathname === link.to ? '#06b6d4' : 'white',
                    textDecoration: 'none',
                    fontWeight: location.pathname === link.to ? 'bold' : 'normal',
                    fontSize: '20px',
                    padding: '18px 24px',
                    borderLeft: location.pathname === link.to ? '4px solid #06b6d4' : '4px solid transparent',
                    transition: 'all 0.2s ease',
                    background: location.pathname === link.to ? 'rgba(6, 182, 212, 0.1)' : 'transparent',
                    width: '100%',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== link.to) {
                      e.target.style.background = 'rgba(6, 182, 212, 0.15)';
                      e.target.style.borderLeft = '4px solid rgba(6, 182, 212, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== link.to) {
                      e.target.style.background = 'transparent';
                      e.target.style.borderLeft = '4px solid transparent';
                    }
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Divider */}
            <div style={{
              height: '2px',
              background: 'rgba(6, 182, 212, 0.3)',
              margin: '0 24px'
            }} />
            
            {/* Mobile Auth Section */}
            <div style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flexShrink: 0,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {user ? (
                <>
                  <div style={{
                    padding: '16px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    marginBottom: '8px',
                    wordBreak: 'break-word',
                    textAlign: 'center'
                  }}>
                    {user.email}
                  </div>
                  <Link
                    to="/customer/profile"
                    onClick={handleNavLinkClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px 20px',
                      color: 'white',
                      textDecoration: 'none',
                      background: 'rgba(139, 92, 246, 0.15)',
                      borderLeft: '4px solid rgba(139, 92, 246, 0.5)',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      boxSizing: 'border-box',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(139, 92, 246, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(139, 92, 246, 0.15)';
                    }}
                  >
                    <User size={20} />
                    My Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px 20px',
                      color: '#ef4444',
                      background: 'transparent',
                      border: 'none',
                      borderLeft: '4px solid rgba(239, 68, 68, 0.5)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      boxSizing: 'border-box',
                      justifyContent: 'center',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(239, 68, 68, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  onClick={handleNavLinkClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 20px',
                    background: 'rgba(236, 72, 153, 0.2)',
                    borderLeft: '4px solid #ec4899',
                    borderRadius: '8px',
                    color: '#ec4899',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    boxSizing: 'border-box',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(236, 72, 153, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(236, 72, 153, 0.2)';
                  }}
                >
                  <LogIn size={20} />
                  Login
                </Link>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Portal renders to document.body, completely bypassing App container
  return typeof document !== 'undefined' 
    ? createPortal(menuContent, document.body)
    : null;
};

export default SlideOutMobileMenu;

